import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { formatIdDateTime } from "@/lib/format";
import { CheckCircle, XCircle, Warning, FileText } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const FIELD_LABEL = {
  no_polisi: "No Polisi",
  jumlah: "Jumlah",
  nama_barang: "Nama Barang",
  no_so: "No SO",
  distrik: "Distrik",
  tanggal: "Tanggal",
};

/**
 * Global inbox for mismatch corrections.
 *  - Super Admin (role='admin') sees Approve / Reject actions.
 *  - Admin Kantor sees status only (their own drafts + others').
 *  - Tabs: Pending (default) | Approved | Rejected.
 */
export default function MismatchApprovals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null); // { id, action, no_spb }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isSuperAdmin = user?.role === "admin";

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const r = await api.get("/mismatch/corrections", { params: { status } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const decide = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      await api.post(`/mismatch/corrections/${decision.id}/${decision.action}`, { note: note.trim() });
      toast.success(decision.action === "approve"
        ? `SPB ${decision.no_spb} ter-update & diverifikasi`
        : `Koreksi SPB ${decision.no_spb} ditolak`);
      setDecision(null); setNote("");
      await load(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4" data-testid="mismatch-approvals-page">
      <div className="flex items-center gap-3 mb-6">
        <Warning size={22} weight="fill" className="text-amber-700" />
        <h1 className="text-2xl font-bold">Koreksi Mismatch</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Admin Kantor dan Admin dapat mengajukan koreksi field yang mismatch pada SPB.
        {isSuperAdmin
          ? " Sebagai Super Admin, Anda bisa menyetujui/menolak di sini."
          : " Menunggu persetujuan Super Admin."}
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        {["pending", "approved", "rejected"].map((s) => (
          <TabsContent key={s} value={s}>
            {loading ? (
              <div className="p-6 text-muted-foreground text-sm">Memuat…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-muted-foreground text-sm border border-dashed border-border">
                Belum ada koreksi {s}.
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((c) => (
                  <div key={c.id} className="border border-border bg-card p-4" data-testid={`approval-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <Link
                          to={`/spb/${c.spb_id}`}
                          className="font-mono font-bold text-lg hover:underline flex items-center gap-2"
                        >
                          <FileText size={16} />
                          {c.no_spb}
                        </Link>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Diajukan oleh{" "}
                          <span className="font-semibold text-foreground">
                            {c.submitted_by?.name || c.submitted_by?.email}
                          </span>{" "}
                          ({c.submitted_by?.role}) · {formatIdDateTime(c.submitted_at)}
                        </div>
                      </div>
                      {isSuperAdmin && c.status === "pending" && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => { setDecision({ id: c.id, action: "approve", no_spb: c.no_spb }); setNote(""); }}
                                  data-testid={`approve-${c.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Setujui
                          </Button>
                          <Button size="sm" variant="destructive"
                                  onClick={() => { setDecision({ id: c.id, action: "reject", no_spb: c.no_spb }); setNote(""); }}
                                  data-testid={`reject-${c.id}`}>
                            <XCircle size={14} className="mr-1" /> Tolak
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                      {Object.entries(c.proposed_changes || {}).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-muted-foreground">{FIELD_LABEL[k] || k}:</span>{" "}
                          <span className="line-through opacity-60">{String(c.original_values?.[k] ?? "-")}</span>
                          <span className="mx-1">→</span>
                          <span className="font-bold">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                    {c.reason && (
                      <div className="text-xs italic text-muted-foreground mt-2">"{c.reason}"</div>
                    )}
                    {c.decided_by && (
                      <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
                        {c.status === "approved"
                          ? <CheckCircle size={12} className="inline text-emerald-700 mr-1" />
                          : <XCircle size={12} className="inline text-rose-700 mr-1" />}
                        {c.status === "approved" ? "Disetujui" : "Ditolak"} oleh{" "}
                        <span className="font-semibold">{c.decided_by?.name || c.decided_by?.email}</span>
                        {" · "}{formatIdDateTime(c.decided_at)}
                        {c.decision_note && <span> — "{c.decision_note}"</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="approval-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.action === "approve" ? "Setujui Koreksi" : "Tolak Koreksi"} — SPB {decision?.no_spb}
            </DialogTitle>
            <DialogDescription>
              {decision?.action === "approve"
                ? "Perubahan akan diterapkan ke SPB dan status jadi 'diverifikasi'."
                : "Draft koreksi ditutup, SPB tetap mismatch."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Catatan (opsional)"
            rows={3}
            data-testid="approval-note"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)} disabled={submitting}>Batal</Button>
            <Button onClick={decide} disabled={submitting}
                    variant={decision?.action === "approve" ? "default" : "destructive"}
                    data-testid="approval-confirm">
              {submitting ? "Memproses…" : decision?.action === "approve" ? "Setujui" : "Tolak"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
